import { languages } from '../../configs';

import isDevMode from '../utils/is-devmode';
import api from '../api';

const PUSH_INTERVAL_MS = 15 * 1000;
const MAX_BATCH = 100;

/** @type {Map<string, { key: string, value: string }>} */
const pending = new Map();
/** Keys already queued or successfully pushed this session: `${lang}:${key}` */
const seen = new Set();

let flushTimer = null;
let flushing = false;

const isDorukTechHost = () => {
    if (typeof window === 'undefined' || !window.location) {
        return false;
    }
    const host = (window.location.hostname || '').toLowerCase();
    return host === 'doruk.tech' || host.endsWith('.doruk.tech');
};

export const shouldCollectMissingTranslations = () =>
    isDevMode() || isDorukTechHost();

const scheduleFlush = () => {
    if (flushTimer || flushing) {
        return;
    }
    flushTimer = setTimeout(() => {
        flushTimer = null;
        flushMissingTranslations();
    }, PUSH_INTERVAL_MS);
};

/** Source keys are English phrases or CONSTANT_CASE. Arabic text is a translation value. */
const ARABIC_SCRIPT = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

const isTranslatedText = (val) => typeof val === 'string' && ARABIC_SCRIPT.test(val);

/**
 * Queue a key that is missing from the DB dictionary.
 * @param {string} key
 * @param {string} lang
 * @param {string} [value] fallback / local value to store (defaults to key)
 */
export const queueMissingTranslation = (key, lang, value) => {
    if (!shouldCollectMissingTranslations() || !key || !lang || isTranslatedText(key)) {
        return;
    }

    const id = `${lang}:${key}`;
    if (seen.has(id)) {
        return;
    }

    seen.add(id);
    pending.set(id, {
        key,
        value: value == null || value === '' ? key : String(value),
    });
    scheduleFlush();
};

/**
 * After DB dictionary load: push every bundled (_en/_tr/_aa) key that is
 * absent from the API response. Values come from the local fallback files.
 */
export const queueBundledKeysMissingFromDb = (lang, dbDict) => {
    if (!shouldCollectMissingTranslations() || !lang) {
        return;
    }

    const bundled = languages.bundledI18n?.[lang];
    if (!bundled || typeof bundled !== 'object') {
        return;
    }

    const db = dbDict && typeof dbDict === 'object' ? dbDict : {};

    Object.keys(bundled).forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(db, key)) {
            queueMissingTranslation(key, lang, bundled[key]);
        }
    });
};

const flushMissingTranslations = async () => {
    if (flushing || !pending.size) {
        return;
    }

    flushing = true;
    const batchIds = [];
    const items = [];

    for (const [id, item] of pending) {
        batchIds.push(id);
        items.push(item);
        if (items.length >= MAX_BATCH) {
            break;
        }
    }

    batchIds.forEach((id) => pending.delete(id));

    try {
        const res = await api.config.pushMissingTranslations({ items });
        if (!res?.success) {
            batchIds.forEach((id, i) => {
                seen.delete(id);
                pending.set(id, items[i]);
            });
            scheduleFlush();
        } else if (pending.size) {
            scheduleFlush();
        }
    } catch (e) {
        batchIds.forEach((id, i) => {
            seen.delete(id);
            pending.set(id, items[i]);
        });
        scheduleFlush();
    } finally {
        flushing = false;
    }
};

if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
        if (pending.size) {
            if (flushTimer) {
                clearTimeout(flushTimer);
                flushTimer = null;
            }
            flushMissingTranslations();
        }
    });
}

const isAlreadyTranslatedValue = (val) => {
    const dicts = languages.i18n || {};
    return Object.keys(dicts).some((lang) => {
        const dict = dicts[lang];
        return dict && Object.values(dict).includes(val);
    });
};

/** True when `val` is a dictionary value or already-localized text, not a source key. */
export const isAlreadyTranslated = (val) =>
    isAlreadyTranslatedValue(val) || isTranslatedText(val);

const trans = (val, lang) => {
    if (!val) {
        return null;
    }

    const activeLanguage = lang || document.documentElement.lang || 'tr';
    const dict = languages.i18n[activeLanguage];

    if (dict && Object.prototype.hasOwnProperty.call(dict, val)) {
        return dict[val];
    }

    // trans(trans('GENERAL_ERROR')) and API strings that are already localized
    // (game type names, etc.): a dictionary value, not a key — do not seed it.
    if (isAlreadyTranslated(val)) {
        return val;
    }

    // Not in DB dict and not in bundled fallback for this lang — forgotten key.
    queueMissingTranslation(val, activeLanguage, val);
    return val;
};

export default trans;
